/**
 * src/shell/auth/services/TokenStorage.ts
 *
 * Zero-Trace In-Memory Access Token Storage & Keychain Refresh Token Manager.
 * Tokens live strictly as Uint8Array buffers in memory.
 * On eviction or logout, buffer.fill(0) executes in-place to avoid V8 immutable string leaks.
 */

import { NativeAdapter } from '../../services/NativeAdapter';

export interface StoredSession {
  accountId: string;
  expiresAt: number;
}

export class TokenStorage {
  private static accessTokenBuffer: Uint8Array | null = null;
  private static activeAccountId: string | null = null;
  private static tokenExpiresAt = 0;

  /**
   * Stores access token bytes in memory.
   */
  public static setAccessToken(tokenBytes: Uint8Array, accountId: string, expiresInSeconds = 3600): void {
    this.clearAccessToken(); // Wipe previous buffer first
    this.accessTokenBuffer = new Uint8Array(tokenBytes);
    this.activeAccountId = accountId;
    this.tokenExpiresAt = Date.now() + expiresInSeconds * 1000;
  }

  /**
   * Retrieves access token as Uint8Array reference.
   */
  public static getAccessTokenBytes(): Uint8Array | null {
    if (!this.accessTokenBuffer) return null;
    if (Date.now() > this.tokenExpiresAt) {
      this.clearAccessToken();
      return null;
    }
    return this.accessTokenBuffer;
  }

  /**
   * Formats authorization header only at dispatch moment.
   */
  public static getAuthHeader(): string | null {
    const bytes = this.getAccessTokenBytes();
    if (!bytes) return null;
    return `Bearer ${new TextDecoder().decode(bytes)}`;
  }

  /**
   * Wipes access token in memory with 0s.
   */
  public static clearAccessToken(): void {
    if (this.accessTokenBuffer) {
      this.accessTokenBuffer.fill(0); // In-place zeroization
      this.accessTokenBuffer = null;
    }
    this.activeAccountId = null;
    this.tokenExpiresAt = 0;
  }

  /**
   * Persists refresh token securely via NativeAdapter keychain.
   */
  public static async storeRefreshToken(accountId: string, refreshToken: string): Promise<void> {
    const bytes = new TextEncoder().encode(refreshToken);
    await NativeAdapter.keychain.setSecret(`refresh.${accountId}`, bytes);
  }

  /**
   * Retrieves refresh token from NativeAdapter keychain.
   */
  public static async getRefreshToken(accountId: string): Promise<string | null> {
    const bytes = await NativeAdapter.keychain.getSecret(`refresh.${accountId}`);
    if (!bytes) return null;
    return new TextDecoder().decode(bytes);
  }

  /**
   * Revokes and removes refresh token from keychain.
   */
  public static async deleteRefreshToken(accountId: string): Promise<void> {
    await NativeAdapter.keychain.deleteSecret(`refresh.${accountId}`);
  }

  /**
   * Clears entire session (memory access token + keychain refresh token).
   */
  public static async clearSession(accountId: string): Promise<void> {
    this.clearAccessToken();
    await this.deleteRefreshToken(accountId);
  }
}
